/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import {
  CheckSquare,
  Columns3,
  FileText,
  Import,
  LayoutTemplate,
  MoreVertical,
  PencilIcon,
  Play,
  Save,
  Search,
  Square,
  Trash2,
  Upload,
  X,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Spinner } from "@/components/ui/spinner"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useIsMobile } from "@/hooks/use-mobile"
import { cn } from "@/lib/utils"
import { useParserSearchStore } from "@/stores/parser-search-store"
import { useParserSelectionStore } from "@/stores/parser-selection-store"
import { useParserUploadStore } from "@/stores/parser-upload-store"

import { AddColumnsDialog } from "./add-columns-dialog"
import { SaveTemplateDialog } from "./add-template-dialog"
import { EditTitleDialog } from "./edit-title-dialog"
import { ImportTemplateDialog } from "./import-template-dialog"
import { FilePickerDialog } from "../../chat/components/file-picker-dialog"
import { useAIParserSession } from "../hooks/use-ai-parser-session"
import { useDeleteColumnMutation } from "../hooks/use-delete-columns-mutation"
import { useDeleteDocumentsMutation } from "../hooks/use-delete-documents-mutation"
import { useDirectFileUpload } from "../hooks/use-direct-file-upload"
import { useExportMutation } from "../hooks/use-export-mutation"
import { useGetFullSession } from "../hooks/use-get-full-session"
import { useRunParserMutation } from "../hooks/use-run-parser-mutation"

export function AIParserHeader() {
  const { t } = useTranslation()
  const [isAddColumnsDialogOpen, setIsAddColumnsDialogOpen] = useState(false)
  const [isAddTemplateDialogOpen, setIsAddTemplateDialogOpen] = useState(false)
  const [isImportTemplateDialogOpen, setIsImportTemplateDialogOpen] = useState(false)
  const [isEditTitleDialogOpen, setIsEditTitleDialogOpen] = useState(false)

  const searchInputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<NodeJS.Timeout | null>(null)
  const [localSearchValue, setLocalSearchValue] = useState("")

  const { isSearchActive, searchTerm, setSearchTerm, openSearch, closeSearch } =
    useParserSearchStore()

  useEffect(() => {
    setLocalSearchValue(searchTerm)
  }, [searchTerm])

  useEffect(() => {
    if (isSearchActive) {
      setTimeout(() => searchInputRef.current?.focus(), 0)
    }
  }, [isSearchActive])

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  const handleSearchInputChange = useCallback(
    (value: string) => {
      setLocalSearchValue(value)
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      debounceRef.current = setTimeout(() => {
        setSearchTerm(value)
      }, 150)
    },
    [setSearchTerm]
  )

  const handleSearchClose = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    setLocalSearchValue("")
    closeSearch()
  }, [closeSearch])

  const {
    isFilePickerOpen,
    setIsFilePickerOpen,
    openFilePicker,
    handleLocalFilesSelected,
    handleGraphFileSelect,
    handleDocumentsSelected,
  } = useDirectFileUpload()

  const fullSessionQuery = useGetFullSession({})
  const exportMutation = useExportMutation({
    filename: fullSessionQuery.data?.title,
  })

  const isMobile = useIsMobile({ breakpoint: 768 })
  const { currentSessionId } = useAIParserSession()
  const { runParserMutation, isRunParserPending, jobStatus, activeJobSessionId, cancelCurrentJob } =
    useRunParserMutation()

  const uploadState = useParserUploadStore((state) => state.uploadState)
  const isUploading = uploadState?.isUploading ?? false

  const {
    selectMode,
    selectedFileIndices,
    selectedColumnIds,
    toggleSelectMode,
    selectAllFiles,
    clearAllSelection,
    exitSelectMode,
  } = useParserSelectionStore()
  const deleteDocumentsMutation = useDeleteDocumentsMutation()
  const deleteColumnMutation = useDeleteColumnMutation()
  const files = fullSessionQuery.data?.parser_data.files ?? []
  const validFileIndices = useMemo(() => files.map((_, index) => index), [files])
  const hasSelection = selectedFileIndices.size > 0 || selectedColumnIds.size > 0

  const selectedFilenames = useMemo(() => {
    return Array.from(selectedFileIndices)
      .map((index) => files[index]?.filename)
      .filter((name): name is string => !!name)
  }, [selectedFileIndices, files])

  const selectedColumnIdList = useMemo(() => Array.from(selectedColumnIds), [selectedColumnIds])

  function getSelectionText() {
    const fileCount = selectedFileIndices.size
    const columnCount = selectedColumnIds.size

    if (fileCount > 0 && columnCount > 0) {
      return t("pages.ai_parser.bulk_actions.files_and_columns_selected", {
        files: fileCount,
        columns: columnCount,
      })
    }
    if (fileCount > 0) {
      return t("pages.ai_parser.bulk_actions.files_selected", { count: fileCount })
    }
    if (columnCount > 0) {
      return t("pages.ai_parser.bulk_actions.columns_selected", { count: columnCount })
    }
    return t("pages.ai_parser.bulk_actions.none_selected")
  }

  async function handleDeleteSelected() {
    if (!currentSessionId) return

    if (selectedFilenames.length > 0) {
      await deleteDocumentsMutation.mutateAsync({
        sessionId: currentSessionId,
        filenames: selectedFilenames,
      })
    }

    for (const columnId of selectedColumnIdList) {
      await deleteColumnMutation.mutateAsync(columnId)
    }

    clearAllSelection()
  }

  function handleRunSelected() {
    if (!currentSessionId || selectedFilenames.length === 0) return

    runParserMutation.mutate(
      { sessionId: currentSessionId, background: true, filenames: selectedFilenames },
      { onSuccess: () => exitSelectMode() }
    )
  }

  const currentSessionJobStatus = useMemo(() => {
    if (!jobStatus) return null
    if (jobStatus.sessionId !== currentSessionId) return null
    return jobStatus
  }, [currentSessionId, jobStatus])

  const canCancelCurrentSessionJob = !!activeJobSessionId && activeJobSessionId === currentSessionId

  const openAddColumnsDialog = () => setIsAddColumnsDialogOpen(true)
  const openAddDocumentsDialog = openFilePicker
  const openAddTemplateDialog = () => setIsAddTemplateDialogOpen(true)
  const openEditTitleDialog = () => setIsEditTitleDialogOpen(true)
  const openImportTemplateDialog = () => setIsImportTemplateDialogOpen(true)
  const handleCancelJob = () => void cancelCurrentJob()

  function runParser() {
    if (!currentSessionId) return
    runParserMutation.mutate({ sessionId: currentSessionId, background: true })
  }

  function getTitle() {
    return currentSessionId ? fullSessionQuery.data?.title : t("pages.ai_parser.header.new_session")
  }

  const isRunPending = isRunParserPending || isUploading

  const progressText = useMemo(() => {
    if (!canCancelCurrentSessionJob) return null
    const progress = currentSessionJobStatus?.progress
    if (!progress) return t("pages.ai_parser.progress.initializing")
    if (progress.totalFiles > 0) {
      return t("pages.ai_parser.progress.processing_files", {
        current: progress.processedFiles,
        total: progress.totalFiles,
      })
    }
    return t("pages.ai_parser.progress.initializing")
  }, [canCancelCurrentSessionJob, currentSessionJobStatus, t])

  const hasFilesWithExtractedValues = useMemo(
    () =>
      fullSessionQuery.data?.parser_data.files?.some(
        (file) => Object.keys(file.extracted_values ?? {}).length > 0
      ) ?? false,
    [fullSessionQuery.data]
  )

  const hasFiles = useMemo(
    () =>
      (fullSessionQuery.data?.parser_data.files &&
        fullSessionQuery.data.parser_data.files.length > 0) ??
      false,
    [fullSessionQuery.data]
  )

  const hasColumns = useMemo(
    () =>
      fullSessionQuery.data?.parser_data.columns &&
      fullSessionQuery.data.parser_data.columns.length > 0,
    [fullSessionQuery.data]
  )

  const hasColumnsToParse = useMemo(
    () =>
      fullSessionQuery.data?.parser_data.columns?.some((column) => column.requires_extraction) ??
      false,
    [fullSessionQuery.data]
  )

  const hasRowsToParse = useMemo(
    () =>
      fullSessionQuery.data?.parser_data.files?.some(
        (file) => Object.keys(file.extracted_values ?? {}).length === 0
      ) ?? false,
    [fullSessionQuery.data]
  )

  const shouldDisableRunParser =
    !currentSessionId ||
    !hasFiles ||
    !hasColumns ||
    (!hasColumnsToParse && !hasRowsToParse) ||
    isUploading

  const shouldDisableExport =
    !currentSessionId || !hasFilesWithExtractedValues || exportMutation.isPending

  const shouldDisableAddTemplate = !currentSessionId || !hasColumns

  function renderSearchInput() {
    if (!hasFiles) return null

    if (isSearchActive) {
      return (
        <div
          className={cn(
            "group/search bg-background relative flex h-9 items-center rounded-lg border transition-[width] duration-200",
            "w-48 focus-within:w-64"
          )}
        >
          <Search className="text-muted-foreground absolute left-2.5 h-4 w-4 shrink-0" />
          <Input
            ref={searchInputRef}
            className="placeholder:text-muted-foreground h-full border-none bg-transparent pr-8 pl-8 text-sm shadow-none focus-visible:ring-0"
            placeholder={t("pages.ai_parser.header.search_placeholder")}
            value={localSearchValue}
            onChange={(event) => handleSearchInputChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                handleSearchClose()
              }
            }}
          />
          <Button
            aria-label={t("common.close")}
            className="absolute right-1 h-6 w-6 shrink-0 rounded-full p-0"
            size="icon"
            variant="ghost"
            onClick={handleSearchClose}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )
    }

    return (
      <Button className="h-9 w-9 shrink-0" size="icon" variant="outline" onClick={openSearch}>
        <Search className="h-4 w-4" />
        <span className="sr-only">{t("pages.ai_parser.header.search")}</span>
      </Button>
    )
  }

  function renderActionButtons() {
    if (selectMode) {
      return (
        <>
          <Button
            disabled={validFileIndices.length === 0}
            size="sm"
            variant="outline"
            onClick={() => selectAllFiles(validFileIndices)}
          >
            {selectedFileIndices.size === validFileIndices.length && validFileIndices.length > 0
              ? t("pages.ai_parser.bulk_actions.deselect_all")
              : t("pages.ai_parser.bulk_actions.select_all")}
          </Button>
          {selectedFilenames.length > 0 && (
            <Button disabled={isRunPending} size="sm" onClick={handleRunSelected}>
              {isRunPending ? <Spinner className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              {t("pages.ai_parser.bulk_actions.run_selected")}
            </Button>
          )}
          {hasSelection && (
            <Button
              size="sm"
              variant="destructive"
              disabled={
                deleteDocumentsMutation.isPending || deleteColumnMutation.isPending || isRunPending
              }
              onClick={handleDeleteSelected}
            >
              {deleteDocumentsMutation.isPending || deleteColumnMutation.isPending ? (
                <Spinner className="h-4 w-4" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              {t("pages.ai_parser.bulk_actions.delete")}
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={exitSelectMode}>
            <X className="h-4 w-4" />
            {t("pages.ai_parser.header.done")}
          </Button>
        </>
      )
    }

    if (isMobile) {
      return (
        <>
          {renderSearchInput()}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline">
                <MoreVertical className="h-4 w-4" />
                <span className="sr-only">{t("pages.ai_parser.header.actions")}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem disabled={shouldDisableAddTemplate} onClick={openAddTemplateDialog}>
                <Save className="mr-2 h-4 w-4" />
                {t("pages.ai_parser.header.save_template")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={openImportTemplateDialog}>
                <Import className="mr-2 h-4 w-4" />
                {t("pages.ai_parser.header.import_template")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled={isRunPending} onClick={openAddDocumentsDialog}>
                <FileText className="mr-2 h-4 w-4" />
                {t("pages.ai_parser.header.add_documents")}
              </DropdownMenuItem>
              <DropdownMenuItem disabled={isRunPending} onClick={openAddColumnsDialog}>
                <Columns3 className="mr-2 h-4 w-4" />
                {t("pages.ai_parser.header.add_columns")}
              </DropdownMenuItem>
              <DropdownMenuItem disabled={isRunPending || !hasFiles} onClick={toggleSelectMode}>
                <CheckSquare className="mr-2 h-4 w-4" />
                {t("pages.ai_parser.header.select")}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={isRunPending || shouldDisableExport}
                onClick={() => exportMutation.mutate()}
              >
                {exportMutation.isPending ? (
                  <Spinner className="mr-2 h-4 w-4" />
                ) : (
                  <Upload className="mr-2 h-4 w-4" />
                )}
                {t("pages.ai_parser.header.export")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {canCancelCurrentSessionJob ? (
                <DropdownMenuItem onClick={handleCancelJob}>
                  <Square className="mr-2 h-4 w-4" />
                  {t("pages.ai_parser.header.cancel")}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  disabled={isRunPending || shouldDisableRunParser}
                  onClick={runParser}
                >
                  <Play className="mr-2 h-4 w-4" />
                  {t("pages.ai_parser.header.run")}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )
    }

    return (
      <>
        {renderSearchInput()}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button disabled={isRunPending} size="sm" variant="outline">
              <LayoutTemplate className="h-4 w-4" />
              {t("pages.ai_parser.header.templates")}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem disabled={shouldDisableAddTemplate} onClick={openAddTemplateDialog}>
              <Save className="h-4 w-4" />
              {t("pages.ai_parser.header.save")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={openImportTemplateDialog}>
              <Import className="h-4 w-4" />
              {t("pages.ai_parser.header.import")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          disabled={isRunPending}
          size="sm"
          variant="outline"
          onClick={openAddDocumentsDialog}
        >
          <FileText className="h-4 w-4" />
          {t("pages.ai_parser.header.add_documents")}
        </Button>
        <Button disabled={isRunPending} size="sm" variant="outline" onClick={openAddColumnsDialog}>
          <Columns3 className="h-4 w-4" />
          {t("pages.ai_parser.header.add_columns")}
        </Button>
        <Button
          disabled={isRunPending || !hasFiles}
          size="sm"
          variant="outline"
          onClick={toggleSelectMode}
        >
          <CheckSquare className="h-4 w-4" />
          {t("pages.ai_parser.header.select")}
        </Button>
        <Button
          disabled={isRunPending || shouldDisableExport}
          size="sm"
          variant="outline"
          onClick={() => exportMutation.mutate()}
        >
          {exportMutation.isPending ? (
            <Spinner className="h-4 w-4" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          {t("pages.ai_parser.header.export")}
        </Button>
        {canCancelCurrentSessionJob ? (
          <Button size="sm" variant="destructive" onClick={handleCancelJob}>
            <Square className="h-4 w-4" />
            {t("pages.ai_parser.header.cancel")}
          </Button>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                disabled={isRunPending || shouldDisableRunParser}
                size="sm"
                className={cn(
                  "min-w-[100px] font-semibold shadow-sm transition-shadow",
                  !shouldDisableRunParser &&
                    !isRunPending &&
                    hasRowsToParse &&
                    "animate-subtle-pulse ring-primary/30 ring-2"
                )}
                onClick={runParser}
              >
                {isRunPending ? <Spinner className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                {t("pages.ai_parser.header.run")}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t("pages.ai_parser.header.run_tooltip")}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </>
    )
  }

  return (
    <>
      <div className="border-border bg-background sticky top-0 flex flex-col border-b shadow-sm">
        <div className="flex h-16 items-center justify-between p-3">
          {currentSessionId && fullSessionQuery.isPending ? (
            <Spinner />
          ) : (
            <div className="text-foreground flex items-center gap-2">
              <h2 className="text-lg font-semibold">{getTitle()}</h2>
              {!selectMode && (
                <Button
                  disabled={isRunPending || isUploading}
                  size="icon"
                  variant="ghost"
                  onClick={openEditTitleDialog}
                >
                  <PencilIcon className="h-4 w-4" />
                </Button>
              )}
              {selectMode && (
                <span className="text-muted-foreground text-sm font-medium tabular-nums">
                  · {getSelectionText()}
                </span>
              )}
              {canCancelCurrentSessionJob && !isUploading && (
                <div className="flex items-center gap-2">
                  <div className="bg-primary/10 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium">
                    <span className="animate-shimmer from-primary via-primary/50 to-primary bg-gradient-to-r bg-[length:200%_100%] bg-clip-text text-transparent tabular-nums">
                      {progressText}
                      {currentSessionJobStatus?.progress.totalFiles &&
                        currentSessionJobStatus.progress.totalFiles > 0 &&
                        ` (${Math.round(
                          (currentSessionJobStatus.progress.processedFiles /
                            currentSessionJobStatus.progress.totalFiles) *
                            100
                        )}%)`}
                    </span>
                  </div>
                </div>
              )}
              {isUploading && uploadState && (
                <div className="bg-warning/30 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium">
                  <span className="animate-shimmer from-warning via-warning to-warning bg-gradient-to-r bg-[length:200%_100%] bg-clip-text text-transparent">
                    {t("pages.ai_parser.header.uploading_files", {
                      count: uploadState.totalFiles,
                    })}
                  </span>
                </div>
              )}
            </div>
          )}
          <div className="flex items-center gap-2">{renderActionButtons()}</div>
        </div>
      </div>

      <AddColumnsDialog
        hasFiles={hasFiles}
        isOpen={isAddColumnsDialogOpen}
        onOpenChange={setIsAddColumnsDialogOpen}
      />
      <FilePickerDialog
        open={isFilePickerOpen}
        source="recent"
        onDocumentsSelected={handleDocumentsSelected}
        onLocalFilesSelected={handleLocalFilesSelected}
        onOpenChange={setIsFilePickerOpen}
        onSelect={handleGraphFileSelect}
      />
      <SaveTemplateDialog
        isOpen={isAddTemplateDialogOpen}
        onOpenChange={setIsAddTemplateDialogOpen}
      />
      <EditTitleDialog
        currentTitle={fullSessionQuery.data?.title ?? t("pages.ai_parser.header.new_session")}
        isOpen={isEditTitleDialogOpen}
        sessionId={currentSessionId}
        onOpenChange={setIsEditTitleDialogOpen}
      />
      <ImportTemplateDialog
        isOpen={isImportTemplateDialogOpen}
        onOpenChange={setIsImportTemplateDialogOpen}
      />
    </>
  )
}
